// client/src/config/settingsRegistry.ts (converted from .js, ST-00207)
//
// Settings Registry - Single source of truth for all settings
// This file defines the structure of the settings hub page (SettingsHome). A card is shown
// only to users who may open its page: SettingsHome resolves `/settings${item.route}`
// through config/pagePermissions.

import {
  Palette,
  Mail,
  FileText,
  Users,
  BarChart3,
  Settings as SettingsIcon,
  AlertTriangle,
  Database,
  ClipboardList,
  Bell,
  KeyRound,
  Satellite,
  type LucideIcon,
} from 'lucide-react';

export type SettingsSection = 'organization' | 'module';

/** One settings page (a card entry). */
export interface SettingsItem {
  /** Unique identifier */
  id: string;
  /** Display name */
  label: string;
  /** Search keywords */
  keywords: string[];
  /** Route path (relative to /settings) */
  route: string;
  /** Short description */
  description: string;
  /** Whether the feature is implemented */
  implemented?: boolean;
  /**
   * App roles of which the user needs one to see the card (ST-00222). Admin-tier roles
   * hold every permission key ('*'), so a page whose API answers only global admins
   * cannot be gated by a permission key alone.
   */
  requiredAppRoles?: readonly string[];
}

/** A card: a group of settings pages. */
export interface SettingsCategory {
  /** Unique identifier */
  id: string;
  /** Display name */
  label: string;
  /** Lucide icon component */
  icon: LucideIcon;
  section: SettingsSection;
  /** Category items */
  items: SettingsItem[];
}

/** An item with its category, as the search and the hub list it. */
export interface SettingsSearchItem extends SettingsItem {
  categoryLabel: string;
  categoryId: string;
  categoryIcon: LucideIcon;
  section: SettingsSection;
}

/** Complete settings registry */
export const settingsRegistry: SettingsCategory[] = [
  // ============================================
  // ORGANIZATION SETTINGS
  // ============================================
  {
    id: 'branding',
    label: 'Branding',
    icon: Palette,
    section: 'organization',
    items: [
      {
        id: 'logo',
        label: 'Logo',
        keywords: ['logo', 'branding', 'image', 'upload', 'company'],
        route: '/organization/branding/logo',
        description: 'Upload and manage organization logos',
        implemented: true,
      },
      {
        id: 'theme',
        label: 'Theme & Colors',
        keywords: [
          'theme',
          'colors',
          'appearance',
          'ui',
          'branding',
          'dark',
          'light',
          'mode',
          'contrast',
        ],
        route: '/organization/branding/theme',
        description: 'Customize application theme and colors',
        implemented: true,
      },
    ],
  },
  {
    id: 'communications',
    label: 'Communications',
    icon: Mail,
    section: 'organization',
    items: [
      {
        id: 'email',
        label: 'Email Configuration',
        keywords: ['email', 'smtp', 'mail', 'configuration', 'zeptomail'],
        route: '/organization/communications/email',
        description: 'Configure SMTP settings and email delivery',
        implemented: true,
      },
      {
        id: 'signatures',
        label: 'Signatures',
        keywords: ['signatures', 'email', 'footer', 'template'],
        route: '/organization/communications/signatures',
        description: 'Manage email signatures and templates',
        implemented: true,
      },
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: Bell,
    section: 'organization',
    items: [
      {
        id: 'notification-channels',
        label: 'Notification Channels',
        keywords: [
          'notifications',
          'push',
          'alerts',
          'bell',
          'sound',
          'desktop',
          'email',
          'browser',
          'pwa',
        ],
        route: '/organization/notifications',
        // Not LexiStar's notification rules (Settings → LexiStar → Notifications, ST-00252).
        description:
          'How LexiBridge reaches you: push notifications on this device, in-app and sound alerts',
        implemented: true,
      },
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: FileText,
    section: 'organization',
    items: [
      {
        id: 'pdf-templates',
        label: 'PDF Templates',
        keywords: ['pdf', 'templates', 'generator', 'documents', 'reports'],
        route: '/organization/documents/pdf-templates',
        description: 'Create and manage PDF report templates',
        implemented: true,
      },
    ],
  },
  {
    id: 'users-access',
    label: 'Users & Access',
    icon: Users,
    section: 'organization',
    items: [
      {
        id: 'users',
        label: 'User Management',
        keywords: ['users', 'accounts', 'people', 'staff'],
        route: '/organization/users/manage',
        description: 'Manage user accounts and profiles',
        implemented: true,
      },
      {
        id: 'roles',
        label: 'Roles & Permissions',
        keywords: ['roles', 'permissions', 'access', 'security', 'authorization'],
        route: '/organization/users/roles',
        description: 'Configure roles and access permissions',
        implemented: true,
      },
    ],
  },
  {
    id: 'account-setup',
    label: 'Account Setup',
    icon: SettingsIcon,
    section: 'organization',
    items: [
      {
        id: 'tenant-details',
        label: 'Organization Details',
        keywords: ['tenant', 'organization', 'company', 'details', 'profile', 'account'],
        route: '/organization/account-setup/tenant',
        description: 'View and edit organization details and contact information',
        implemented: true,
      },
      {
        id: 'subscription-usage',
        label: 'Subscription & Usage',
        keywords: ['subscription', 'usage', 'plan', 'limits', 'billing', 'quota'],
        route: '/organization/account-setup/subscription',
        description: 'View subscription plan and resource usage statistics',
        implemented: true,
      },
      {
        id: 'route-groupings',
        label: 'Route Groupings',
        keywords: [
          'route',
          'groupings',
          'locations',
          'dispatch',
          'receipt',
          'organization',
          'setup',
        ],
        route: '/organization/account-setup/route-groupings',
        description: 'Create and manage route groupings for analysis',
        implemented: true,
      },
      {
        id: 'tenant-management',
        label: 'Tenant Management',
        keywords: [
          'tenant',
          'management',
          'organization',
          'create',
          'suspend',
          'admin',
          'location',
          'ownership',
          'dispatch',
          'receipt',
          'site',
          'visibility',
        ],
        route: '/organization/account-setup/tenant-management',
        description: 'Manage tenant organizations and location ownership (global admin)',
        implemented: true,
        requiredAppRoles: ['global-admin'],
      },
    ],
  },

  {
    id: 'integrations',
    label: 'Integrations',
    icon: KeyRound,
    section: 'organization',
    items: [
      {
        id: 'api-keys',
        label: 'API Keys',
        keywords: [
          'api',
          'keys',
          'odata',
          'excel',
          'power query',
          'power bi',
          'feed',
          'token',
          'integration',
          'refresh',
        ],
        route: '/organization/integrations/api-keys',
        description: 'Generate and manage your personal OData API keys for Excel / Power BI',
        implemented: true,
      },
    ],
  },

  // ============================================
  // MODULE SETTINGS
  // ============================================
  {
    id: 'reporting',
    label: 'Reporting',
    icon: BarChart3,
    section: 'module',
    items: [
      {
        id: 'custom-summaries',
        label: 'Custom Summaries',
        keywords: ['custom', 'summaries', 'reports', 'templates'],
        route: '/modules/reporting/custom-summaries',
        description: 'Configure custom summary reports',
        implemented: true,
      },
      {
        id: 'email-reports',
        label: 'Email Reports',
        keywords: ['email', 'reports', 'schedules', 'automated'],
        route: '/modules/reporting/email-reports',
        description: 'Set up automated email report schedules',
        implemented: true,
      },
      {
        id: 'telegram-bi',
        label: 'Telegram BI',
        keywords: ['telegram', 'bot', 'bi', 'analytics', 'messaging'],
        route: '/modules/reporting/telegram-bi',
        description: 'Configure Telegram bot for BI delivery',
        implemented: true,
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: SettingsIcon,
    section: 'module',
    items: [
      {
        id: 'amendments',
        label: 'Amendments',
        keywords: ['amendments', 'changes', 'modifications', 'data'],
        route: '/modules/operations/amendments',
        description: 'Configure data amendment workflows',
        implemented: true,
      },
      {
        id: 'transaction-allocation',
        label: 'Transaction Allocation',
        keywords: ['transaction', 'allocation', 'assignment', 'routing'],
        route: '/modules/operations/transaction-allocation',
        description: 'Manage transaction allocation rules',
        implemented: false,
      },
    ],
  },
  {
    id: 'order-management',
    label: 'Order Management',
    icon: ClipboardList,
    section: 'module',
    items: [
      {
        id: 'order-master-data',
        label: 'Master Data',
        keywords: [
          'orders',
          'products',
          'locations',
          'companies',
          'master',
          'data',
          'dispatch',
          'receipt',
        ],
        route: '/modules/order-management/master-data',
        description: 'Manage products, locations, companies, and routes',
        implemented: true,
      },
    ],
  },
  // LexiStar (ST-00240, ST-00252): every LexiStar setting on one card — notification setup
  // and usage & billing first (the two the customer asked to find here), then the set-up of
  // the fleet, zones, routes and fuel, then the ways data leaves. These came from Monitoring;
  // the pages keep their /modules/monitoring/... addresses (bookmarks, review-queue fix links).
  {
    id: 'lexistar',
    label: 'LexiStar',
    icon: Satellite,
    section: 'module',
    items: [
      // ST-00188 M3-U2; its own entry and label since ST-00252; "Notification setup" since
      // ST-00251 (the sidebar's "Notifications" is the inbox of received notifications).
      {
        id: 'lexistar-notifications',
        label: 'Notification setup',
        keywords: [
          'lexistar',
          'notification',
          'notifications',
          'notification rules',
          'notification setup',
          'rule',
          'rules',
          'alert',
          'alerts',
          'escalation',
          'recipient',
          'group',
          'template',
          'whatsapp',
          'telegram',
          'sms',
          'budget',
          'quiet hours',
          'digest',
          'reminder',
        ],
        route: '/modules/monitoring/lexistar-notifications',
        description:
          'Who gets told about what, on which channel: notification rules, recipient groups, message templates, channel budgets and SLAs for LexiStar events',
        implemented: true,
      },
      // ST-00207 M4b-A; here since ST-00240 (opens /lexistar/billing)
      {
        id: 'lexistar-billing',
        label: 'Usage & billing',
        keywords: [
          'lexistar',
          'usage',
          'billing',
          'statement',
          'usage statement',
          'invoice',
          'active trucks',
          'messages',
          'vat',
          'pdf',
        ],
        route: '/modules/lexistar/usage-billing',
        description:
          "Your organisation's monthly LexiStar usage statement: active trucks, messages, VAT and the PDF",
        implemented: true,
      },
      {
        id: 'lexistar-assets',
        label: 'Assets',
        keywords: [
          'lexistar',
          'asset',
          'assets',
          'fleet',
          'truck',
          'horse',
          'trailer',
          'loader',
          'registration',
          'alias',
          'ownership',
          'transfer',
          'transporter',
        ],
        route: '/modules/monitoring/lexistar-assets',
        description:
          'Trucks, trailers and loaders by owning organisation: registrations, aliases and ownership transfers',
        implemented: true,
      },
      {
        id: 'telemetry-devices',
        label: 'Devices',
        keywords: [
          'lexistar',
          'telemetry',
          'gps',
          'imei',
          'device',
          'devices',
          'tracker',
          'satellite',
          'fleet',
          'loader',
          'teltonika',
          'registry',
        ],
        route: '/modules/monitoring/telemetry-devices',
        description: 'The GPS tracker registry for Fleet and Loaders: IMEIs, classes and registrations',
        implemented: true,
      },
      // ST-00276 phase 2: how the trucks look on Live View, the control room, Replay and Trips
      {
        id: 'lexistar-map-colours',
        label: 'Map colours & states',
        keywords: [
          'lexistar',
          'map',
          'colour',
          'colours',
          'color',
          'colors',
          'state',
          'states',
          'legend',
          'marker',
          'badge',
          'ring',
          'pulse',
          'display',
          'live view',
          'control room',
          'fuel',
          'rules',
        ],
        route: '/modules/lexistar/map-colours',
        description:
          'How your trucks look on Live View, the control room, Replay and the Trips view: colour rules, marks for alerts, and their history',
        implemented: true,
      },
      // ST-00249: the codes maintenance bookings, breakdowns and delays are booked with
      {
        id: 'lexistar-maintenance-codes',
        label: 'Maintenance & delay codes',
        keywords: [
          'lexistar',
          'maintenance',
          'breakdown',
          'delay',
          'code',
          'codes',
          'reason',
          'workshop',
          'service',
          'downtime',
          'availability',
          'time model',
        ],
        route: '/modules/lexistar/maintenance-codes',
        description:
          'The codes a truck’s time is booked with — planned maintenance, breakdown or delay — their order and whether they are offered',
        implemented: true,
      },
      // ST-00248: the targets the actual figures are compared with (opens Fleet & targets → Targets)
      {
        id: 'lexistar-targets',
        label: 'Performance targets',
        keywords: [
          'lexistar',
          'target',
          'targets',
          'plan',
          'planned',
          'budget',
          'goal',
          'kpi',
          'trips',
          'tonnes',
          'fuel',
          'utilisation',
          'availability',
          'actual vs target',
        ],
        route: '/modules/lexistar/targets',
        description:
          'The targets per day, week or month for trips, tonnes, kilometres, fuel, on-time trips, utilisation and availability that the actual figures are compared with (opens Fleet & targets)',
        implemented: true,
      },
      {
        id: 'geofencing',
        label: 'Geofences',
        keywords: [
          'lexistar',
          'geofencing',
          'geofence',
          'geofences',
          'zone',
          'zones',
          'boundary',
          'trips',
          'events',
          'aerial',
          'drone',
        ],
        route: '/modules/monitoring/geofencing',
        description:
          'Zones and their rules, vehicle trips, geofence events, time in zones and drone imagery (opens the Geofences page)',
        implemented: true,
      },
      {
        id: 'lexistar-routes',
        label: 'Routes & Lanes',
        keywords: [
          'lexistar',
          'route',
          'routes',
          'lane',
          'lanes',
          'corridor',
          'golden run',
          'advisory',
          'deviation',
          'weighbridge',
          'permitted stop',
          'toll',
        ],
        route: '/modules/monitoring/lexistar-routes',
        description:
          'Directional lanes and their approved routes: engine-generated or golden-run lines, corridors, stops and advisories',
        implemented: true,
      },
      {
        id: 'lexistar-fuel',
        label: 'Fuel Norms & Prices',
        keywords: [
          'lexistar',
          'fuel',
          'diesel',
          'norm',
          'norms',
          'consumption',
          'litres',
          'price',
          'rate card',
          'cost',
          'allowance',
          'budget',
        ],
        route: '/modules/monitoring/lexistar-fuel',
        description:
          'Consumption norms by class, asset and load state, diesel prices by region and rate cards, effective-dated',
        implemented: true,
      },
      // ST-00207 M4b-B: signed outbound webhooks
      {
        id: 'lexistar-webhooks',
        label: 'Integrations & webhooks',
        keywords: [
          'lexistar',
          'integration',
          'integrations',
          'webhook',
          'webhooks',
          'api',
          'callback',
          'endpoint',
          'signature',
          'hmac',
          'secret',
          'events',
          'replay',
        ],
        route: '/modules/monitoring/lexistar-webhooks',
        description:
          'Send LexiStar events to your own systems: signed HTTPS webhooks with retries, a delivery log and replay',
        implemented: true,
      },
      // ST-00207 M4b-C: per-tenant data export
      {
        id: 'lexistar-export',
        label: 'Data export',
        keywords: [
          'lexistar',
          'export',
          'download',
          'data',
          'csv',
          'zip',
          'backup',
          'popia',
          'trips',
          'positions',
          'drivers',
          'messages',
        ],
        route: '/modules/monitoring/lexistar-export',
        description:
          "Download your organisation's LexiStar data as CSV files in one ZIP: fleet, drivers, routes, trips, events, messages and positions",
        implemented: true,
      },
    ],
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    icon: AlertTriangle,
    section: 'module',
    items: [
      {
        id: 'error-detection',
        label: 'Error Detection',
        keywords: ['error', 'detection', 'alerts', 'monitoring', 'rules'],
        route: '/modules/monitoring/error-detection',
        description: 'Configure error detection rules and alerts',
        implemented: true,
      },
    ],
  },
  {
    id: 'database',
    label: 'Database',
    icon: Database,
    section: 'module',
    items: [
      {
        id: 'performance',
        label: 'Performance',
        keywords: ['database', 'performance', 'indexes', 'optimization'],
        route: '/modules/database/performance',
        description: 'View the indexes and size of the ticket tables (global admin)',
        implemented: true,
        requiredAppRoles: ['global-admin'],
      },
    ],
  },
];

/** Get all settings items (flattened), each with its category. */
export const getAllSettingsItems = (): SettingsSearchItem[] => {
  return settingsRegistry.flatMap((category) =>
    category.items.map((item) => ({
      ...item,
      categoryLabel: category.label,
      categoryId: category.id,
      categoryIcon: category.icon,
      section: category.section,
    }))
  );
};

/** Search settings by query (label, category, description, keywords; case-insensitive). */
export const searchSettings = (query: string | null | undefined): SettingsSearchItem[] => {
  if (!query || query.trim() === '') {
    return getAllSettingsItems();
  }

  const lowerQuery = query.toLowerCase().trim();
  const allItems = getAllSettingsItems();

  return allItems.filter((item) => {
    const searchText = [item.label, item.categoryLabel, item.description, ...item.keywords]
      .join(' ')
      .toLowerCase();

    return searchText.includes(lowerQuery);
  });
};

/** Get settings by section. */
export const getSettingsBySection = (section: SettingsSection): SettingsCategory[] => {
  return settingsRegistry.filter((category) => category.section === section);
};

/** Find a settings item by route (relative to /settings). */
export const findSettingByRoute = (route: string): SettingsSearchItem | undefined => {
  return getAllSettingsItems().find((item) => item.route === route);
};
